import { useMemo } from 'react';
// Custom hooks
import usePrevious from './usePrevious';
import useWindowDimensions from './useWindowDimensions';
import useTouchFocus from './useTouchFocus';
// Sub-components
import NodesLabels from './NodesLabels';
import TripRibbons from './TripRibbons';
import TripCaption from './TripCaption';
import PersonCaption from './PersonCaption';
import TripsBar from './TripsBar';
import HammockDiaryLink from './HammockDiaryLink';
// Tools
import { no_propagate } from './utils';

const MARGIN = 100;

const ChordDiagram = ({ people, trips, segment_gap, num_trips,
                        highlighted_person, highlighted_trip,
                        focused_person, focused_trip, selected_person, selected_trip,
                        set_focused_person, set_focused_trip, set_selected_person, set_selected_trip,
                        order_options, people_order_descr, update_people_order,
                        animating, hammock_diary_urls }) => {

  // ***************************************************************************
  // * Layout
  // ***************************************************************************
  const { height: vh, width: vw } = useWindowDimensions();
  // portrait layout:
  //  _________
  // |         |
  // | 900x900 |
  // |_________|
  // | 900*100 |
  // |_________|
  // | 900*100 |
  // |_________|   total: 900 * 1100
  //
  // landscape layout:
  //  ____________
  // |         |  |
  // | 900x900 |  | 100*900
  // |_________|__|
  // |  1000*100  |
  // |____________|   total: 1000x1000
  //
  // If vw > vh, use landscape (margins at left and right)
  // If vh > vw*1100/900, use portrait (margins at top and bottom)
  // Else:
  //   // portrait viewport but almost square
  //   svg_diam_landscape := width*0.9
  //   svg_diam_portrait := height*0.9/1.1
  //   cross over point when these two are equal
  //   i.e. width*0.9 == height*0.9/1.1
  //   i.e. height == width*1.1
  let height, width, orient, centre_x, centre_y, order_buttons_margin;
  if (vh > vw * 1.1) {
    // Portrait
    orient = "portrait";
    if (vh > vw*1.1/0.9) {
      // Margins on top and bottom
      width = 900;
      height = width*vh/vw;
      order_buttons_margin = vh*0.5 + vw*0.5;
    } else {
      // Margins on left and right
      height = 1100;
      width = height*vw/vh;
      order_buttons_margin = vh/1.1;
    }
    centre_x = width/2;
    centre_y = height/2 - 100;
  } else {
    // Landscape
    // Height is 1100, width is based off that.
    orient = "landscape";
    if (vw > vh) {
      // Margins on left and right
      height = 1000;
      width = height*vw/vh;
    } else {
      // Margins on top and bottom.
      width = 1000;
      height = width*vh/vw;
    }
    centre_x = width/2 - 50;
    centre_y = height/2 - 50;
    order_buttons_margin = vh*0.9;
  }
  const radius_off_screen = Math.sqrt((height - centre_y)**2 + (width - centre_x)**2);

  const radius = 450 - MARGIN;
  // Trips bar
  const trips_bar_offset = MARGIN+10;
  const trips_bar_translate_str = (orient === "portrait") ?
                                  `translate(0, ${radius + trips_bar_offset})` :
                                  `translate(${radius + trips_bar_offset}, 0)`;

  // ***************************************************************************
  // * Touch-related event handlers
  // ***************************************************************************
  const { on_touch_move, on_touch_end } = useTouchFocus({
    focused_person, focused_trip,
    set_focused_person, set_focused_trip,
    set_selected_person, set_selected_trip,
    animating
  });

  // Hammock diary url
  let hammock_diary_url = '';
  if (focused_trip !== null) {
    hammock_diary_url = hammock_diary_urls[focused_trip];
  } else if (selected_trip !== null) {
    hammock_diary_url = hammock_diary_urls[selected_trip];
  }

  const prev_selected_person = usePrevious(selected_person);
  const selected_person_change = selected_person !== prev_selected_person;

  const trip_initial_anim_delays = useMemo(() => [...Array(num_trips)].map(Math.random), [num_trips]);

  return (
    <>
      <div id="svg_container" className={orient}>
        <svg viewBox={`0 0 ${width} ${height}`}
             onTouchMove={on_touch_move}
             onTouchEnd={on_touch_end}>
          <g transform={`translate(${centre_x}, ${centre_y})`}>
            <g id="nodes_labels"><NodesLabels people={people}
                                              radius={radius}
                                              radius_off_screen={radius_off_screen}
                                              highlighted_person={highlighted_person}
                                              highlighted_trip={highlighted_trip}
                                              segment_gap={segment_gap}
                                              selected_person_change={selected_person_change}
                                              set_focused_person={set_focused_person}
                                              set_selected_person={set_selected_person}
                                              animating={animating}/></g>
            <g id="trip_ribbons"><TripRibbons num_trips={num_trips}
                                              trips={trips}
                                              radius={radius}
                                              highlighted_trip={highlighted_trip}
                                              highlighted_person={highlighted_person}
                                              selected_person_change={selected_person_change}
                                              initial_anim_delays={trip_initial_anim_delays}
                                              set_focused_trip={set_focused_trip}
                                              set_selected_trip={set_selected_trip}/></g>
            <g id="trip_caption"><TripCaption trip={highlighted_trip !== null ? trips[highlighted_trip] : null}/></g>
            <g id="person_caption"><PersonCaption person={highlighted_person !== null ? people[highlighted_person] : null}/></g>
            <g id="trips_bar" transform={trips_bar_translate_str}>
              <TripsBar orient={orient}
                        trips={trips}
                        hammock_diary_urls={hammock_diary_urls}
                        length={2*radius + 50}
                        highlighted_trip={highlighted_trip}
                        highlighted_person={highlighted_person}
                        selected_trip={selected_trip}
                        selected_person={selected_person}
                        trip_initial_anim_delays={trip_initial_anim_delays}
                        set_focused_trip={set_focused_trip}
                        set_selected_trip={set_selected_trip}/>
            </g>
          </g>
        </svg>
      </div>
      <div id="non_svg_container_footer">
        <div className="slide_in_container" style={{marginTop : order_buttons_margin}}>
          <div className="order_buttons slide_in_up">
            <button id="order_button_intro">
              Order by:
            </button>
            {order_options.map((option) => (
              <button key={option.key} className="order_button"
                      onClick={no_propagate(() => update_people_order(option.key))}
                      disabled={people_order_descr === option.key}>
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <HammockDiaryLink url={hammock_diary_url}/>
      </div>
    </>
  );
};

export default ChordDiagram;
